import type { ComponentType, ReactNode } from 'react'

/**
 * The navigation icon set.
 *
 * ── Why these are drawn rather than imported ──
 * The rail used stock lucide icons and they came from several idioms at once: a piggy bank for
 * Deposit, a bank building for Lend, a lightning bolt for Lite. Drawing them means one stroke
 * weight, one set of terminals and one level of detail across the whole rail.
 *
 * ── Why they are CONVENTIONAL and not clever ──
 * An earlier version drew these as machine-hall metaphors — Deposit an inlet valve, Lend a
 * reservoir with a level, Home a three-legged manifold. It was coherent and it was wrong for
 * this surface. A rail icon is read in peripheral vision by someone who wants to get somewhere,
 * and a metaphor you have to be TAUGHT costs them a beat every time until they learn it. A
 * house means home to everyone alive; a manifold means home to whoever read the comment.
 *
 * Identity belongs where there is room to carry it: the page-header art (ui/page-art.tsx) is
 * filled, two-tone and distinctive, and it appears once per page at 40px where a reader has
 * already arrived and is not hunting. Navigation is not the place to be interesting.
 *
 * ── Conventions, so a fourteenth icon matches the thirteen ──
 *   · 24×24 viewBox and a 1.75 stroke, matching lucide's box so these are drop-in replacements
 *     and any remaining lucide icon elsewhere in the app still sits beside them correctly.
 *   · `currentColor` only. The rail inverts to white on the active enamel tile, so an icon
 *     that hardcodes a colour breaks in exactly one state and nobody notices until it ships.
 *   · No more than four elements each. These render at 18px, where a fifth becomes mud.
 *   · Round caps everywhere — the same terminals as the logo. That is the family resemblance.
 */

function Icon({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {children}
    </svg>
  )
}

/** Two paths crossing in opposite directions — an exchange, not a refresh. */
export const IconSwap = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M4 9h13" />
    <path d="M14 6l3 3-3 3" />
    <path d="M20 15H7" />
    <path d="M10 18l-3-3 3-3" />
  </Icon>
)

export const IconDeposit = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M12 3.5v9.4" />
    <path d="m8.2 9.4 3.8 3.8 3.8-3.8" />
    <path d="M4.2 15.4v3.4a1.4 1.4 0 0 0 1.4 1.4h12.8a1.4 1.4 0 0 0 1.4-1.4v-3.4" />
  </Icon>
)

export const IconBond = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <rect x="3.6" y="4.6" width="16.8" height="12.6" rx="2" />
    <path d="M7.2 8.6h6.2" />
    <path d="M7.2 12.2h3.8" />
    <circle cx="16.6" cy="13" r="2.4" />
  </Icon>
)

export const IconPositions = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="m12 3.3 8.2 4.1-8.2 4.1L3.8 7.4z" />
    <path d="m3.8 12 8.2 4.1 8.2-4.1" />
    <path d="m3.8 16.6 8.2 4.1 8.2-4.1" />
  </Icon>
)

export const IconLite = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M4 8h11.2" />
    <circle cx="17.6" cy="8" r="2.4" />
    <path d="M20 16H8.8" />
    <circle cx="6.4" cy="16" r="2.4" />
  </Icon>
)

export const IconLend = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <ellipse cx="12" cy="6.4" rx="7.6" ry="2.9" />
    <path d="M4.4 6.4v5.2c0 1.6 3.4 2.9 7.6 2.9s7.6-1.3 7.6-2.9V6.4" />
    <path d="M4.4 11.6v5.2c0 1.6 3.4 2.9 7.6 2.9s7.6-1.3 7.6-2.9v-5.2" />
  </Icon>
)

export const IconBorrow = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M12 12.9V3.5" />
    <path d="m8.2 7.1 3.8-3.6 3.8 3.6" />
    <path d="M4.2 15.4v3.4a1.4 1.4 0 0 0 1.4 1.4h12.8a1.4 1.4 0 0 0 1.4-1.4v-3.4" />
  </Icon>
)

export const IconMultiply = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M20.2 12a8.2 8.2 0 1 1-2.4-5.8" />
    <path d="M20.4 3.6v4.9h-4.9" />
  </Icon>
)

export const IconMarkets = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M4 20V13.4" />
    <path d="M9.3 20V8.2" />
    <path d="M14.7 20v-8.4" />
    <path d="M20 20V4.4" />
  </Icon>
)

export const IconBcdx = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="8.3" />
    <path d="M12 7.2v9.6" />
    <path d="M9.6 9.4h3.6a1.9 1.9 0 0 1 0 3.8H9.6h3.9a1.9 1.9 0 0 1 0 3.8H9.6" />
  </Icon>
)

export const IconProof = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M12 3.4 19 6v5.6c0 4.2-2.8 7.4-7 9-4.2-1.6-7-4.8-7-9V6z" />
    <path d="m9.1 11.9 2.1 2.1 4-4.1" />
  </Icon>
)

export const IconPortfolio = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <rect x="3.2" y="7.2" width="17.6" height="12.6" rx="2" />
    <path d="M8.8 7.2V5.6a1.6 1.6 0 0 1 1.6-1.6h3.2a1.6 1.6 0 0 1 1.6 1.6v1.6" />
    <path d="M3.2 12.4h17.6" />
  </Icon>
)

/**
 * Home: the hall itself — a pitched roof over the capital line.
 *
 * Not the stock house glyph with a door in it. This is the machine hall's gable and the pipe
 * running under it, so the one icon that means "everything, at a glance" is still drawn from
 * the same world as the eleven that mean "this specific instrument".
 */
export const IconHome = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M3.6 10.6 12 4l8.4 6.6" />
    <path d="M5.6 12.1V19a1 1 0 0 0 1 1h10.8a1 1 0 0 0 1-1v-6.9" />
    <path d="M9.6 20v-5.2h4.8V20" />
  </Icon>
)

/**
 * A stable identity for each icon, used to pair it with its page-header art.
 *
 * ── Why a string and not the component itself ──
 * `page-art.tsx` first keyed its lookup by the component reference. That is typo-proof, which
 * was the argument for it, and it is also WRONG in two ways that both fail silently:
 *
 *   · Hot reload swaps the module. After an edit here, the art map still held the previous
 *     component objects while the pages received the new ones, so every lookup missed and the
 *     header quietly rendered the line icon. It looks like a design decision, not a fault.
 *   · Code-splitting can instantiate a module more than once. Two copies of `IconHome` are two
 *     different references and will never match, on a build that only happens in production.
 *
 * A string survives both. Pages never write these — they pass the icon and the id travels with
 * it — so there is still nothing to misspell at a call site.
 *
 * Assigned explicitly rather than read from `Component.name`, which a minifier rewrites.
 */
export type NavIcon = ComponentType<{ className?: string }> & { iconId?: string }

const ID: [NavIcon, string][] = [
  [IconHome, 'home'],
  [IconSwap, 'swap'],
  [IconDeposit, 'deposit'],
  [IconBond, 'bond'],
  [IconPositions, 'positions'],
  [IconLite, 'lite'],
  [IconLend, 'lend'],
  [IconBorrow, 'borrow'],
  [IconMultiply, 'multiply'],
  [IconMarkets, 'markets'],
  [IconBcdx, 'bcdx'],
  [IconProof, 'proof'],
  [IconPortfolio, 'portfolio'],
]
for (const [icon, id] of ID) icon.iconId = id

/**
 * The BNB Chain mark, as bscscan.com serves it.
 *
 * Source: bscscan.com/assets/bsc/images/svg/logos/chain-light.svg — their compact chain icon,
 * 256x256, four paths, geometry unmodified. It replaces a hand-drawn approximation of the
 * five-diamond Binance mark, which was the wrong logo anyway: that is the exchange's, this is
 * the chain's.
 *
 * (`logo-light.svg` is the BscScan WORDMARK — 466x122 including the lettering, and 18KB. It
 * cannot be a chip icon at that aspect, and its text paths are `fill="black"`, so it would
 * render invisible on this ground.)
 *
 * ── Why this one breaks the file's rules on purpose ──
 * Everything above is a 1.75 stroke on a 24 box, because that is what makes the navigation set
 * look drawn by one hand. This is not navigation — it is somebody else's LOGO, and a brand mark
 * redrawn in our stroke weight and box stops being their mark. The viewBox is theirs.
 *
 * ── Why it is not BNB gold ──
 * The source paths are #F0B90B. This renders in `currentColor` instead, because the palette has
 * no yellow in it any more and one gold mark in the corner would be the only warm thing on
 * screen. Set the fills back to #F0B90B if brand accuracy is ever worth more than that.
 *
 * Inlined rather than hotlinked: an <img> to bscscan would be a third-party request on every
 * page load, would break offline, and would fail closed under any strict CSP.
 */
export const IconBnb = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 256 256" fill="currentColor" className={className} aria-hidden>
    <path d="M45.975 128.248L46.1072 176.697L87.2743 200.922V229.287L22.0147 191.012V114.08L45.975 128.248ZM45.975 79.7989V108.032L22 93.8492V65.6165L45.975 51.4341L70.0675 65.6165L45.975 79.7989ZM104.466 65.6165L128.441 51.4341L152.534 65.6165L128.441 79.7989L104.466 65.6165Z" />
    <path d="M63.2991 166.655V138.29L87.2741 152.473V180.705L63.2991 166.655ZM104.466 211.082L128.441 225.264L152.534 211.082V239.314L128.441 253.497L104.466 239.314V211.082ZM186.918 65.6165L210.893 51.4341L234.985 65.6165V93.8492L210.893 108.032V79.7989L186.918 65.6165ZM210.893 176.697L211.025 128.248L235 114.066V190.997L169.741 229.272V200.907L210.893 176.697Z" />
    <path d="M193.701 166.655L169.726 180.706V152.473L193.701 138.291V166.655Z" />
    <path d="M193.701 89.8415L193.833 118.206L152.548 142.431V190.998L128.573 205.048L104.598 190.998V142.431L63.3138 118.206V89.8415L87.3915 75.6591L128.427 100.001L169.711 75.6591L193.804 89.8415H193.701ZM63.2991 41.407L128.441 3L193.701 41.407L169.726 55.5894L128.441 31.2474L87.2741 55.5894L63.2991 41.407Z" />
  </svg>
)
